
















































import { Component, Vue, Watch } from 'vue-property-decorator'
import { ReferralStatus, ReferralType } from '@/api/types'
import { ReportAPI } from '@/api/client/report'
import { namespace } from 'vuex-class'
import { hexToRgb } from '@/helpers/color'
const referrals = namespace('referrals')

@Component
export default class ReferralsBlock extends Vue {
    @referrals.Action applyFilters: (ReferralsStateFilters) => Promise<void>
    loading: boolean = false
    referralsCount: {[key: string]: number} = {}
    groupBy: string = null
    view: string = 'user'

    mounted() {
        this.groupBy = this.availableGroupBy[0]
        this.view = this.$currentUser.defaultDashboardView
        this.loadReferralsCount()
    }

    get availableGroupBy(): string[] {
        return ['status', 'type']
    }

    get availableGroupByOptions() {
        return this.availableGroupBy
            .map(availableGroupBy => ({
                label: this.$t(`dashboard.referrals.groupBy.${availableGroupBy}`),
                value: availableGroupBy,
            }))
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`dashboard.referrals.view.${availableView}`),
                value: availableView,
            }))
    }

    @Watch('groupBy')
    @Watch('view')
    reloadReferralsCount() {
        this.loadReferralsCount()
    }

    async loadReferralsCount(): Promise<void> {
        this.loading = true
        this.referralsCount = await ReportAPI.getReferralsCount(this.groupBy, null, this.view)
        this.loading = false
    }

    get filteredReferralsCount(): {[key: string]: number} {
        const referralsCount: {[key: string]: number} = {}
        this.groups.forEach((group: string) => {
            referralsCount[group] = 0
        })

        for (const group in this.referralsCount) {
            if (this.referralsCount[group] > 0) {
                referralsCount[group] = this.referralsCount[group]

                if ('open' in referralsCount && this.isOpenStatus(group)) {
                    referralsCount.open += this.referralsCount[group]
                }
            }
        }

        return referralsCount
    }

    isOpenStatus(status: string): boolean {
        const openStatuses = [
            ReferralStatus.NEW,
            ReferralStatus.PENDING,
            ReferralStatus.PROPOSAL,
            ReferralStatus.UNREACHABLE,
            ReferralStatus.APPOINTMENT,
            ReferralStatus.ON_HOLD,
        ]

        return openStatuses.includes(status as ReferralStatus)
    }

    get groups(): string[] {
        if (this.groupBy === 'status') {
            return this.statusGroups
        } else if (this.groupBy === 'type') {
            return this.typeGroups
        }

        return []
    }

    get statusGroups(): string[] {
        return [
            ReferralStatus.OPEN,
            ReferralStatus.NEW,
            ReferralStatus.UNREACHABLE,
            ReferralStatus.PENDING,
            ReferralStatus.APPOINTMENT,
            ReferralStatus.PROPOSAL,
            ReferralStatus.ON_HOLD,
            ReferralStatus.PROCESSED,
        ]
    }

    get typeGroups(): string[] {
        return [
            ReferralType.APPRAISAL,
            ReferralType.EPA,
            ReferralType.INFORMATION,
            ReferralType.MORTGAGE,
            ReferralType.PURCHASE,
            ReferralType.PHOTO,
            ReferralType.SALE,
            ReferralType.SIGN_PLACEMENT,
            ReferralType.TAXATION,
            ReferralType.TECHNICAL_APPRAISAL,
            ReferralType.NEW_BUILD,
        ].filter((type: ReferralType) => this.$currentCompany.handledReferralTypes.includes(type))
    }

    formatGroup(group: string): string {
        if (this.groupBy === 'status') {
            return this.formatStatus(group)
        } else if (this.groupBy === 'type') {
            return this.formatType(group)
        }

        return group
    }

    formatStatus(status: string): string {
        if (status === 'open') {
            status = 'all'
        }

        return this.$t(`referral.status.${status}`).toString()
    }

    formatType(type: string): string {
        return this.$t(`referral.type.${type}`).toString()
    }

    colorForGroup(group: string, opacity: number = 1): string {
        let colors = {}

        if (this.groupBy === 'status') {
            colors = this.colorsForStatus(opacity)
        } else if (this.groupBy === 'type') {
            colors = this.colorsForType(opacity)
        }

        return colors[group] || `rgba(96, 96, 96, ${opacity})`
    }

    colorsForStatus(opacity: number = 1): { [key: string]: string } {
        return {
            open: `rgba(96, 96, 96, ${opacity})`,
            [ReferralStatus.NEW]: `rgba(19, 210, 197, ${opacity})`,
            [ReferralStatus.UNREACHABLE]: `rgba(235, 89, 140, ${opacity})`,
            [ReferralStatus.PENDING]: `rgba(247, 131, 35, ${opacity})`,
            [ReferralStatus.APPOINTMENT]: `rgba(66, 153, 225, ${opacity})`,
            [ReferralStatus.PROPOSAL]: `rgba(145, 105, 226, ${opacity})`,
            [ReferralStatus.ON_HOLD]: `rgba(231, 220, 30, ${opacity})`,
            [ReferralStatus.SUCCESSFUL]: `rgba(0, 163, 137, ${opacity})`,
            [ReferralStatus.CLOSED]: `rgba(222, 64, 119, ${opacity})`,
            [ReferralStatus.PROCESSED]: `rgba(169, 50, 38, ${opacity})`,
        }
    }

    colorsForType(opacity: number = 1): { [key: string]: string } {
        const availableColors = [
            `rgba(96, 96, 96, ${opacity})`,
            `rgba(169, 50, 38, ${opacity})`,
            `rgba(19, 210, 197, ${opacity})`,
            `rgba(235, 89, 140, ${opacity})`,
            `rgba(247, 131, 35, ${opacity})`,
            `rgba(66, 153, 225, ${opacity})`,
            `rgba(145, 105, 226, ${opacity})`,
            `rgba(231, 220, 30, ${opacity})`,
            `rgba(0, 163, 137, ${opacity})`,
            `rgba(222, 64, 119, ${opacity})`,
        ]

        const colors = {}
        this.typeGroups.forEach((type, index) => {
            colors[type] = availableColors[index]
        })

        return colors
    }

    iconForGroup(group: string): string {
        return 'icon-pending'
    }

    async goToList(group: string) {
        const filters = {
            direction: 'in',
            view: this.view,
            statusFilter: null,
            typeFilter: [],
        }

        group = group === 'open' ? null : group

        if (this.groupBy === 'status') {
            filters.statusFilter = group
        } else if (this.groupBy === 'type') {
            filters.typeFilter = [group]
        }

        await this.applyFilters({
            ...filters,
            dateRangeFilter: 'all',
        })
        await this.$router.push({name: 'tasks_all'})
    }
}
